import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import appConstants from '../constants/appConstants';
import { Table, Button } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { FaDownload, FaArrowLeft, FaChartBar } from 'react-icons/fa';

const getAuthToken = () => localStorage.getItem('authToken');

const TermExamMarksView = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const term_exam_id = searchParams.get('term_exam_id');
  const batch_id = searchParams.get('batch_id');

  const [students, setStudents] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [marks, setMarks] = useState([]);
  const [evaluationType, setEvaluationType] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchExamMarks = async () => {
    setLoading(true);
    const token = getAuthToken();
    try {
      const res = await axios.post(
        `${appConstants.API_ROOT_URL}get_term_exam_marks-v2`,
        { term_exam_id, batch_id },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (res.data.status === 'success') {
        const studentData = res.data.data || [];
        const examEvaluationType = res.data.evaluation_type || 'mark';

        setStudents(studentData);
        setEvaluationType(examEvaluationType.toLowerCase());

        // Extract unique subjects from the first student
        if (studentData.length > 0) {
          const subjectList = studentData[0].marks.map((m) => ({
            id: m.subject_id,
            name: m.subject_name,
            max_mark: m.max_mark,
          }));
          setSubjects(subjectList);
        }
      } else {
        alert(res.data.message || 'Failed to load data.');
      }
    } catch (err) {
      console.error('Error fetching marks:', err);
      alert('Something went wrong.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExamMarks();
  }, [term_exam_id, batch_id]);

  const getMark = (student, subjectId) => {
    const markEntry = student.marks.find((m) => m.subject_id === subjectId);
    return markEntry?.mark_scored ?? '';
  };

  const handleDownload = () => {
    if (!students.length || !subjects.length) return;

    const data = students.map((stu, index) => {
      const row = {
        No: index + 1,
        Name: stu.name,
      };

      subjects.forEach((subj) => {
        const mark = getMark(stu, subj.id);
        const numMark = parseFloat(mark);
        // If it's a number, use it as number; else keep original
        row[subj.name] = !isNaN(numMark) ? numMark : mark;
      });

      // Only add Total column for mark-based exams
      if (evaluationType === 'mark') {
        row['Total'] = getTotal(stu);
      }
      return row;
    });

    const worksheet = XLSX.utils.json_to_sheet(data);

    // Optionally, apply number formatting explicitly
    const range = XLSX.utils.decode_range(worksheet['!ref']);
    for (let R = 1; R <= students.length; ++R) {
      for (let C = 2; C < 2 + subjects.length + 1; ++C) {
        const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
        const cell = worksheet[cellAddress];
        if (cell && typeof cell.v === 'number') {
          cell.t = 'n'; // Mark as numeric
        }
      }
    }

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Term Marks');

    const excelBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });

    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
    });

    saveAs(blob, `TermExam_Marks_${batch_id}_${term_exam_id}.xlsx`);
  };

  const getTotal = (student) => {
    return student.marks.reduce((sum, m) => {
      const val = m.mark_scored;
      const num = parseFloat(val);
      return !isNaN(num) ? sum + num : sum;
    }, 0);
  };

  return (
    <Layout>
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <>
          {/* Compact Top Bar */}
          <div
            style={{
              background: '#343a40',
              color: '#fff',
              borderRadius: 8,
              padding: '0.6rem 1rem',
              marginBottom: 20,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
              <div
                style={{
                  fontSize: 18,
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <FaChartBar />
                Term Exam Marks Report
              </div>
              <div style={{ fontSize: 13, opacity: 0.9 }}>
                {students.length} students • {subjects.length} subjects
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Button
                onClick={handleDownload}
                variant="outline-light"
                size="sm"
                style={{
                  borderRadius: 6,
                  padding: '6px 12px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: 13,
                  fontWeight: 500,
                }}
              >
                <FaDownload />
                Download
              </Button>
              <Button
                onClick={() => navigate(-1)}
                variant="outline-light"
                size="sm"
                style={{
                  borderRadius: 6,
                  padding: '6px 10px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <FaArrowLeft />
              </Button>
            </div>
          </div>

          {/* Table Section */}
          <div
            style={{
              background: '#fff',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              overflow: 'hidden',
            }}
          >
            <div className="table-responsive">
              <Table hover className="mb-0">
                <thead
                  style={{
                    background: '#f8f9fa',
                    borderBottom: '3px solid #dee2e6',
                  }}
                >
                  <tr>
                    <th
                      style={{
                        padding: '12px 20px',
                        fontWeight: '700',
                        color: '#212529',
                        fontSize: '0.95rem',
                        width: '60px',
                        position: 'sticky',
                        left: 0,
                        background: '#f8f9fa',
                        zIndex: 10,
                        letterSpacing: '0.5px',
                      }}
                    >
                      #
                    </th>
                    <th
                      style={{
                        padding: '12px 20px',
                        fontWeight: '700',
                        color: '#212529',
                        fontSize: '0.95rem',
                        minWidth: '200px',
                        position: 'sticky',
                        left: '60px',
                        background: '#f8f9fa',
                        zIndex: 10,
                        letterSpacing: '0.5px',
                      }}
                    >
                      Student Name
                    </th>
                    {subjects.map((subj) => (
                      <th
                        key={subj.id}
                        style={{
                          padding: '12px 20px',
                          fontWeight: '700',
                          color: '#212529',
                          fontSize: '0.95rem',
                          minWidth: '120px',
                          textAlign: 'center',
                          letterSpacing: '0.5px',
                        }}
                      >
                        <div>{subj.name}</div>
                        {subj.max_mark && (
                          <div
                            style={{
                              fontSize: '0.75rem',
                              fontWeight: '500',
                              color: '#6c757d',
                              marginTop: '4px',
                            }}
                          >
                            Max: {subj.max_mark}
                          </div>
                        )}
                      </th>
                    ))}
                    {evaluationType === 'mark' && (
                      <th
                        style={{
                          padding: '12px 20px',
                          fontWeight: '700',
                          color: '#212529',
                          fontSize: '0.95rem',
                          minWidth: '100px',
                          textAlign: 'center',
                          background: '#e7f3ff',
                          letterSpacing: '0.5px',
                        }}
                      >
                        Total
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {students.map((stu, index) => (
                    <tr
                      key={stu.student_id}
                      style={{ borderBottom: '1px solid #f0f0f0' }}
                    >
                      <td
                        style={{
                          padding: '14px 20px',
                          verticalAlign: 'middle',
                          color: '#6c757d',
                          fontWeight: '600',
                          position: 'sticky',
                          left: 0,
                          background: '#fff',
                          zIndex: 5,
                        }}
                      >
                        {index + 1}
                      </td>
                      <td
                        style={{
                          padding: '14px 20px',
                          verticalAlign: 'middle',
                          fontWeight: '600',
                          color: '#2c3e50',
                          position: 'sticky',
                          left: '60px',
                          background: '#fff',
                          zIndex: 5,
                        }}
                      >
                        {stu.name}
                      </td>
                      {subjects.map((subj) => {
                        const mark = getMark(stu, subj.id);
                        return (
                          <td
                            key={subj.id}
                            style={{
                              padding: '14px 20px',
                              verticalAlign: 'middle',
                              textAlign: 'center',
                              fontWeight: '600',
                              color: '#2c3e50',
                              fontSize: '0.95rem',
                            }}
                          >
                            {mark || '-'}
                          </td>
                        );
                      })}
                      {evaluationType === 'mark' && (
                        <td
                          style={{
                            padding: '14px 20px',
                            verticalAlign: 'middle',
                            textAlign: 'center',
                            fontWeight: '700',
                            color: '#667eea',
                            fontSize: '1rem',
                            background: '#f8f9ff',
                          }}
                        >
                          {getTotal(stu)}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>

          {/* Footer Info */}
          <div
            style={{
              marginTop: '20px',
              padding: '16px 20px',
              background: '#f8f9fa',
              borderRadius: '8px',
              border: '1px solid #e9ecef',
            }}
          >
            <p
              className="mb-0"
              style={{ fontSize: '0.9rem', color: '#6c757d' }}
            >
              <strong>Note:</strong> Empty marks are displayed as "-".
            </p>
          </div>
        </>
      )}
    </Layout>
  );
};

export default TermExamMarksView;
